import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth, useShowTimesDrawer, useSideMenu } from '@/logic/state';

import { ALLOW_NAVBAR_ITEMS_PREFETCH } from '@/constants/prefetch';
import CitiesMenu from './cities-menu';
import Icon24 from '../shared/icon-24';
import Link from 'next/link';
import NotificationsPopoverContent from './notifications/notifications-popover-content';
import { PATHS } from '@/constants/routes';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state/dialog';
import { useNotifications } from '@/hooks/use-notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import ImageBoxFuture from '../shared/image-box-future';
// import { SideMenu } from '../side-menu/side-menu';
import dynamic from 'next/dynamic';

const SideMenu = dynamic(
  () => import('../side-menu/side-menu').then(component => component.SideMenu),
  { ssr: false }
);

const NotificationRedDottedIcon = '/icons/navbar/notification-red-dotted-icon.svg';
const NotificationIcon = '/icons/navbar/notification-icon.svg';
const SearchIcon = '/icons/shared/search.svg';
const SearchIconPrimary = '/icons/shared/search-primary.svg';
const MenuIcon = '/icons/navbar/menu.svg';
const AvatarImg = '/icons/side-menu/avatar.svg';
const CalenderIcon = '/icons/navbar/calender.svg';

export const Navbar: FC = () => {
  const navListItems = [
    { title: 'movieFinder', link: PATHS.FINDER },
    { title: 'experiences', link: PATHS.EXPERIENCES },
    { title: '', link: '/' },
    { title: 'cinemas', link: PATHS.CINEMAS },
    { title: 'offers', link: PATHS.OFFERS },
  ];
  const theme = useTheme();
  const intl = useIntl();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('mediumDesktop'));
  const setSideMenu = useSideMenu(s => s.setSideMenu, shallow);
  const user = useAuth(s => s.user, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);
  const setShowTimesDrawer = useShowTimesDrawer(s => s.setShowTimesDrawer, shallow);
  const { isAllNotificationsAreRead, refetch: refetchNotifications } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openNotificationsPopover = (event: React.MouseEvent<HTMLButtonElement>): void => {
    refetchNotifications();
    setAnchorEl(event.currentTarget);
  };

  const closeNotificationsPopover = (): void => {
    setAnchorEl(null);
  };

  const handleUserAvatarClick = () => {
    router.push(PATHS.USER_AREA);
  };

  const checkIfLinkIsActive = (path: string): boolean => {
    const currentRoute = router.pathname.split('/');
    if (!currentRoute || !currentRoute?.length) return;
    if ('/' + currentRoute[1] === (path as PATHS)) {
      return true;
    }
    return false;
  };

  const openedNotificationPopover = Boolean(anchorEl);

  const refetch = (): Promise<void> =>
    queryClient.invalidateQueries([{ url: 'auth/me' }]);

  useEffect(() => {
    user && refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <AppBar
      elevation={0}
      sx={{
        backgroundColor: theme.palette.common.white,
        position: 'sticky',
        top: '0px',
        zIndex: 1101,
        display: router.pathname === PATHS.UNDER_MAINTENANCE ? 'none' : 'block',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          justifyContent: 'space-between',
          paddingLeft: {
            mobile: '8px',
            mediumDesktop: '56px',
          },
          minHeight: 'auto !important',
          '& > *': {
            marginRight: '20px',
          },
          '& > *:last-of-type': {
            marginRight: '0px',
          },
        }}
      >
        <Stack direction='row' alignItems='center' spacing={4.375}>
          <IconButton onClick={() => setSideMenu(true)}>
            <Icon24 icon={MenuIcon} title='alt.menu_icon' />
          </IconButton>
          <SideMenu />
          <CitiesMenu />
        </Stack>
        <Box component='nav'>
          <Stack
            direction='row'
            component='ul'
            alignItems='center'
            spacing={{
              mobile: 3,
              extraLargeDesktop: 5.5,
            }}
          >
            {navListItems.map((item, index) =>
              index === 0 ? (
                <Box
                  component='li'
                  key={item.title}
                  sx={{
                    listStyle: 'none',
                    display: {
                      mobile: 'none',
                      mediumDesktop: 'list-item',
                    },
                  }}
                >
                  <Link
                    href={{ pathname: item.link }}
                    passHref
                    prefetch={ALLOW_NAVBAR_ITEMS_PREFETCH}
                  >
                    <Button
                      component='a'
                      fullWidth={false}
                      startIcon={
                        <Icon24
                          icon={
                            checkIfLinkIsActive(item.link)
                              ? SearchIconPrimary
                              : SearchIcon
                          }
                          title='alt.share_ticket_icon'
                        />
                      }
                      sx={{
                        fontSize: pxToRem(20),
                        letterSpacing: pxToRem(0.1),
                        color: checkIfLinkIsActive(item.link)
                          ? theme.palette.primary.main
                          : theme.palette.grey[60],
                        fontWeight: checkIfLinkIsActive(item.link) && '900',
                      }}
                    >
                      <FormattedMessage id={`nav.link.${item.title}`} />
                    </Button>
                  </Link>
                </Box>
              ) : index === 2 ? (
                <Box
                  component='li'
                  key={item.title}
                  sx={{
                    listStyle: 'none',
                    marginLeft: {
                      mobile: '0px !important',
                      mediumDesktop: '44px !important',
                    },
                  }}
                >
                  <Link
                    href={{ pathname: '/' }}
                    passHref
                    prefetch={ALLOW_NAVBAR_ITEMS_PREFETCH}
                  >
                    <Button component='a' fullWidth={false}>
                      <ImageBoxFuture
                        img='/icons/logos/muvi-logo.svg'
                        title={intl.formatMessage({
                          id: 'images.title.logo',
                        })}
                        objectFit='contain'
                        sx={{
                          marginY: '8px',
                          width: isMobileScreen ? '64px' : '138px',
                          height: isMobileScreen ? '40px' : '84px',
                        }}
                      />
                    </Button>
                  </Link>
                </Box>
              ) : (
                <Box
                  component='li'
                  key={item.title}
                  sx={{
                    listStyle: 'none',
                    display: {
                      mobile: 'none',
                      mediumDesktop: 'list-item',
                    },
                  }}
                >
                  <Link
                    href={{ pathname: item.link }}
                    passHref
                    prefetch={ALLOW_NAVBAR_ITEMS_PREFETCH}
                  >
                    <Button
                      component='a'
                      fullWidth={false}
                      sx={{
                        fontSize: pxToRem(20),
                        letterSpacing: pxToRem(0.1),
                        color: checkIfLinkIsActive(item.link)
                          ? theme.palette.primary.main
                          : theme.palette.grey[60],
                        fontWeight: checkIfLinkIsActive(item.link) && '900',
                      }}
                    >
                      <FormattedMessage id={`nav.link.${item.title}`} />
                    </Button>
                  </Link>
                </Box>
              )
            )}
          </Stack>
        </Box>
        <Stack direction='row' alignItems='center' alignSelf='stretch'>
          {user ? (
            <Stack
              direction='row'
              alignItems='center'
              spacing={5}
              sx={{
                display: {
                  mobile: 'none',
                  mediumDesktop: 'flex',
                },
              }}
            >
              <IconButton onClick={openNotificationsPopover}>
                <Icon24
                  icon={
                    isAllNotificationsAreRead()
                      ? NotificationIcon
                      : NotificationRedDottedIcon
                  }
                  title={
                    isAllNotificationsAreRead()
                      ? 'alt.notification_icon'
                      : 'alt.notification_red_dotted_icon'
                  }
                />
              </IconButton>

              <Popover
                id='notification-popover'
                open={openedNotificationPopover}
                anchorEl={anchorEl}
                onClose={closeNotificationsPopover}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <NotificationsPopoverContent
                  handleCloseNotifications={closeNotificationsPopover}
                />
              </Popover>
              <Link
                href={{
                  pathname: PATHS.USER_AREA,
                }}
                passHref
                prefetch={ALLOW_NAVBAR_ITEMS_PREFETCH}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={2}
                  component='a'
                  sx={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <Avatar
                    sx={{
                      width: '64px',
                      height: '64px',
                    }}
                  >
                    <ImageBoxFuture
                      imageId={'navbar-profile'}
                      img={user.photoUrl ?? AvatarImg}
                      title={intl.formatMessage({
                        id: 'alt.user_image',
                      })}
                      objectFit='cover'
                      sx={{
                        width: '100%',
                        height: '100%',
                      }}
                    />
                  </Avatar>
                  <Typography
                    component='span'
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: pxToRem(20),
                      fontFamily: 'co-head-line-corp-bold',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <FormattedMessage
                      id={
                        user && user.firstName
                          ? 'sideMenu.loggedInUserGreet'
                          : 'sideMenu.guestGreet'
                      }
                      values={{ name: user?.firstName }}
                    />
                  </Typography>
                </Stack>
              </Link>
            </Stack>
          ) : (
            <Button
              id='login-button'
              variant='outlined'
              fullWidth={false}
              sx={{
                paddingY: '16px',
                paddingX: '19px',
                display: {
                  mobile: 'none',
                  mediumDesktop: 'flex',
                },
                marginRight: '2px',
              }}
              onClick={() =>
                setDialog(prevState => ({
                  ...prevState,
                  wrapper: true,
                  isPhoneNumberForm: true,
                }))
              }
            >
              <FormattedMessage id='nav.login.signup' />
            </Button>
          )}
          <Stack
            justifyContent='center'
            sx={{
              backgroundColor: theme.palette.primary.main,
              alignSelf: 'stretch',
              paddingRight: {
                mobile: '16px',
                mediumDesktop: '64px',
              },
              paddingLeft: {
                mobile: '46px',
                mediumDesktop: '114px',
              },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-1px',
                width: {
                  mobile: '30px',
                  mediumDesktop: '50px',
                },
                height: '100%',
                borderTopRightRadius: '50%',
                borderBottomRightRadius: '50%',
                backgroundColor: theme.palette.common.white,
              },
            }}
          >
            <Button
              startIcon={<Icon24 icon={CalenderIcon} title='alt.calender_icon' />}
              fullWidth={false}
              onClick={() => setShowTimesDrawer(true)}
              sx={{
                color: theme.palette.common.white,
                fontSize: {
                  mobile: pxToRem(13),
                  mediumDesktop: pxToRem(20),
                },
                letterSpacing: {
                  mediumDesktop: pxToRem(0.1),
                },
                '& .MuiButton-startIcon': {
                  marginRight: {
                    mediumDesktop: '14px',
                  },
                },
                ':hover': {
                  color: 'white',
                },
              }}
            >
              <FormattedMessage id='nav.link.showTimes' />
            </Button>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
